import type { ReactElement, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Calendar } from '@savvy-kit/components/calendar';
import { Input } from '@savvy-kit/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { format, isValid } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { DATE_INPUT_FORMAT, formatDateInput, parseTypedDate } from './date-mask';

const DEFAULT_DISPLAY_FORMAT = 'MM/dd/yyyy';

// Reach past react-day-picker's today-minus-100-year default; minDate can still narrow the floor.
const YEAR_DROPDOWN_SPAN = 120;

export type DatePickerTriggerArgs = {
  readonly displayValue: string;
  readonly isOpen: boolean;
};

export type DatePickerProps = {
  /** Currently selected date. `undefined` renders the placeholder. */
  readonly value: Date | undefined;
  /** Called when the user picks (or clears) a date. */
  readonly onChange: (date: Date | undefined) => void;
  /**
   * When true, render a keyable text input (masked to `MM/DD/YYYY`) with the
   * calendar demoted to an in-field icon button. Defaults to false (button-trigger).
   *
   * Typing is `MM/DD/YYYY`-only and ignores `displayFormat` and `renderTrigger`.
   * Don't enable it with a non-`MM/dd/yyyy` `displayFormat` (e.g. `MMM yyyy`) — file
   * a task for other formats first.
   */
  readonly allowTyping?: boolean;
  /** Called when the popover closes (the field-level blur analog). Fires on close, not on open. */
  readonly onBlur?: () => void;
  /** Called when the popover opens. For callers that must settle conflicting state before a pick. */
  readonly onOpen?: () => void;
  /** When set and a date is selected, renders a Clear button in the popover footer that unsets the value and closes. */
  readonly clearable?: boolean;
  /** Label for the `clearable` button. Defaults to `Clear`. */
  readonly clearLabel?: string;
  /** Placeholder text shown when no date is selected. */
  readonly placeholder?: string;
  /** date-fns format string for the trigger display. Defaults to `MM/dd/yyyy`. */
  readonly displayFormat?: string;
  /** Earliest selectable date (also constrains the year dropdown). */
  readonly minDate?: Date;
  /** Latest selectable date (also constrains the year dropdown). */
  readonly maxDate?: Date;
  /** Month to show when the picker opens with no value set. */
  readonly defaultMonth?: Date;
  readonly disabled?: boolean;
  /** Class name applied to the default trigger button. Ignored when `renderTrigger` is supplied. */
  readonly triggerClassName?: string;
  /** ARIA label for the trigger when no visible label is supplied by the caller. */
  readonly ariaLabel?: string;
  /** Applied to the default trigger button so an external `<label htmlFor>` associates with it. Ignored when `renderTrigger` is supplied. */
  readonly id?: string;
  /** Forwarded as `aria-describedby` on the trigger so an external error/help element is announced. */
  readonly ariaDescribedby?: string;
  /** Forwarded as `aria-invalid` on the trigger so assistive tech announces the error state. */
  readonly ariaInvalid?: boolean;
  /** Forwarded as `aria-required` on the trigger so assistive tech announces the field is required. */
  readonly ariaRequired?: boolean;
  /**
   * Override the default trigger. Consumers return a single element that becomes
   * the popover trigger DOM — Popover's render slot fuses click/focus/aria
   * wiring onto it. Receives the formatted display string and the popover's
   * current open state so the consumer can render their own label.
   */
  readonly renderTrigger?: (args: DatePickerTriggerArgs) => ReactElement;
  /** Optional node rendered inside the popover under the calendar. Useful for a "Clear" or "Reset" affordance. */
  readonly footer?: ReactNode;
  /** Class name forwarded to `PopoverContent`. Overrides the default `w-96 p-0`. */
  readonly popoverContentClassName?: string;
  /** Class name forwarded to `PopoverContent`'s positioner. Use to lift the popover above modal backdrops (e.g. `z-[1400]`). */
  readonly popoverPositionerClassName?: string;
  /** Class name forwarded to the inner `<Calendar>`. Overrides the default `w-96 [--cell-size:--spacing(10)]` (lets consumers ship a compact variant). */
  readonly calendarClassName?: string;
};

export function DatePicker({
  value,
  onChange,
  onBlur,
  onOpen,
  allowTyping = false,
  clearable = false,
  clearLabel = 'Clear',
  placeholder = 'MM/DD/YYYY',
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  minDate,
  maxDate,
  defaultMonth,
  disabled = false,
  triggerClassName,
  ariaLabel,
  id,
  ariaDescribedby,
  ariaInvalid,
  ariaRequired,
  renderTrigger,
  footer,
  popoverContentClassName,
  popoverPositionerClassName,
  calendarClassName,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [typedText, setTypedText] = useState<string | null>(null);
  // In typing mode the popover trigger is the right-edge icon, so anchor the
  // calendar to the whole field instead — otherwise it aligns to the icon and
  // drifts right of the button-trigger variant.
  const fieldRef = useRef<HTMLDivElement>(null);
  const currentValue = value && isValid(value) ? value : undefined;
  const displayValue = currentValue ? format(currentValue, displayFormat) : placeholder;
  // Floor the year dropdown at `minDate` when given, otherwise reach back a full
  // span so distant birth years stay reachable (the 1926-cutoff bug).
  const startMonth = minDate ?? new Date(new Date().getFullYear() - YEAR_DROPDOWN_SPAN, 0, 1);

  const handleSelect = (next: Date | undefined) => {
    onChange(next);
    setTypedText(null);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) onOpen?.();
    else onBlur?.();
  };

  const inputText = typedText ?? (currentValue ? format(currentValue, DATE_INPUT_FORMAT) : '');

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {allowTyping ? (
        <div className="relative" ref={fieldRef}>
          <Input
            id={id}
            value={inputText}
            onChange={(event) => {
              const masked = formatDateInput(event.target.value);
              setTypedText(masked);
              onChange(parseTypedDate(masked));
            }}
            onBlur={() => {
              // Re-derive the canonical display from `value` once a complete date
              // was typed; keep raw text visible while it's still invalid so the
              // user can fix it.
              if (parseTypedDate(inputText)) setTypedText(null);
              onBlur?.();
            }}
            disabled={disabled}
            placeholder={placeholder}
            inputMode="numeric"
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedby}
            aria-invalid={ariaInvalid}
            aria-required={ariaRequired}
            className={cn('pr-10', triggerClassName)}
          />
          <PopoverTrigger
            disabled={disabled}
            render={
              <button
                type="button"
                aria-label="Open calendar"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary outline-none hover:text-dark disabled:cursor-not-allowed disabled:opacity-50"
              />
            }
          >
            <CalendarIcon className="size-4" />
          </PopoverTrigger>
        </div>
      ) : renderTrigger ? (
        <PopoverTrigger
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          render={renderTrigger({ displayValue, isOpen })}
        />
      ) : (
        <PopoverTrigger
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          render={
            <button
              type="button"
              id={id}
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-md border border-light bg-white px-3 py-1 text-left text-sm font-normal text-dark shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-border-light disabled:cursor-not-allowed disabled:opacity-50',
                'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
                triggerClassName,
              )}
            />
          }
        >
          <span className={currentValue ? 'text-dark' : 'text-secondary'}>{displayValue}</span>
          <CalendarIcon className="size-4 text-secondary" />
        </PopoverTrigger>
      )}
      <PopoverContent
        align="start"
        anchor={allowTyping ? fieldRef : undefined}
        className={popoverContentClassName ?? 'w-96 p-0'}
        positionerClassName={popoverPositionerClassName}
      >
        <Calendar
          mode="single"
          selected={currentValue}
          onSelect={handleSelect}
          defaultMonth={currentValue ?? defaultMonth}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={maxDate}
          disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          fixedWeeks
          className={calendarClassName ?? 'w-96 [--cell-size:--spacing(10)]'}
          classNames={{ root: 'border-light' }}
        />
        {clearable && currentValue && (
          <div className="border-t border-light px-3 pt-1 pb-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-[var(--text-secondary)]"
              onClick={() => handleSelect(undefined)}
            >
              {clearLabel}
            </Button>
          </div>
        )}
        {footer}
      </PopoverContent>
    </Popover>
  );
}
